import { keyBy, partition } from 'es-toolkit';

import { LayoutModel, ModelCategory } from '@paragon/contract-library';

import { PathUtilities } from '../grid/PathUtilities';
import { ChildrenFinder } from './ChildrenFinder';

export function getDescendantPaths(parentPath: string, findChildren: ChildrenFinder) {
  return Array.from(iterateDescendants(parentPath, findChildren), ({ path }) => path);
}

export function getComponentDesignDescendantPaths(parentPath: string, findChildren: ChildrenFinder) {
  return Array.from(iterateDescendants(parentPath, findChildren))
    .filter(({ model }) => model.category === ModelCategory.ComponentDesign)
    .map(({ path }) => path);
}

export function getDescendants(parentGuid: string, findChildren: ChildrenFinder) {
  return Array.from(iterateDescendants(parentGuid, findChildren), ({ model }) => model);
}

export function getDescendantGuids(parentGuid: string, findChildren: ChildrenFinder) {
  return getDescendants(parentGuid, findChildren).map((descendant) => descendant.guid);
}

export function getComponentDesignDescendants(parentGuid: string, findChildren: ChildrenFinder) {
  const descendants = getDescendants(parentGuid, findChildren).filter(
    (model) => model.category === ModelCategory.ComponentDesign,
  );
  const [loadedComponents, summaries] = partition(descendants, (model) => model.type === 'componentDesign');
  const componentModels = keyBy(loadedComponents, (componentModel) => componentModel.guid);

  for (const summary of summaries) {
    if (summary.guid in componentModels) {
      continue;
    }

    componentModels[summary.guid] = summary;
  }

  return Object.values(componentModels);
}

export function getComponentDesignDescendantGuids(parentGuid: string, findChildren: ChildrenFinder) {
  return getComponentDesignDescendants(parentGuid, findChildren).map((model) => model.guid);
}

function* iterateDescendants(
  parentPath: string,
  findChildren: ChildrenFinder,
): IterableIterator<{
  readonly model: LayoutModel;
  readonly path: string;
}> {
  const children = findChildren(PathUtilities.getGuid(parentPath)).map((model) => ({
    model,
    path: PathUtilities.createFrom(parentPath, model.guid),
  }));

  yield* children;

  for (const child of children) {
    yield* iterateDescendants(child.path, findChildren);
  }
}
